// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
import { isDarkMode } from '../../provider/types';
import { blue } from '../primitiveColors/blue';
import { common } from '../primitiveColors/common';
import { grey } from '../primitiveColors/grey';
import { mutedBlue } from '../primitiveColors/muted';
import { transparency } from '../primitiveColors/transparency';
/**
 * Figma's `Inverse/*` set: a flip of the regular theme tokens, used for high-contrast
 * surfaces such as an inverted card or toolbar. It exists inside BOTH themes — the light
 * theme's inverse set reads dark, the dark theme's reads light — and `mode` here is the
 * app's theme, exactly as for `background(mode)` and friends.
 *
 * These values are authored independently in Figma and are NOT `background('dark')` etc.
 * Three of them deviate from the equivalently-named opposite-mode token
 * (`background.action`, `background.state.selectedTransparent`, `...selectedTransparent2`),
 * which is why this has to be its own layer rather than an opposite-mode lookup.
 */ var light = {
    background: {
        accent: blue[400],
        action: blue[900],
        actionBold: blue[700],
        level0: grey[950],
        level100: grey[850],
        level200: grey[800],
        level300: grey[700],
        level50: grey[900],
        modalOverlay: transparency.gray.dark[400],
        tooltip: grey[700],
        state: {
            disabled1: grey[600],
            disabled2: grey[500],
            disabled3: grey[400],
            disabledTransparent: transparency.gray.light[100],
            hover0: transparency.gray.light[50],
            hover100: grey[850],
            hover200: grey[800],
            hover300: grey[700],
            hover50: grey[900],
            selected1: mutedBlue[800],
            selected2: mutedBlue[700],
            selected3: mutedBlue[600],
            selectedAlt: transparency.gray.light[100],
            selectedTransparent: transparency.gray.light[200],
            selectedTransparent2: transparency.gray.light[300]
        }
    },
    border: {
        action100: grey[400],
        action200: grey[300],
        actionBold: grey[100],
        active: grey[300],
        default: grey[750],
        focus: grey[50],
        heaviest: grey[50],
        heavy: grey[600],
        soft: grey[800]
    },
    icon: {
        action: blue[500],
        action200: blue[700],
        actionAlt: grey[50],
        active: grey[50],
        default: grey[400],
        disabled: grey[700],
        disabled2: grey[600],
        heaviest: common.white,
        heavy: grey[200],
        soft: grey[500]
    },
    text: {
        accent: blue[300],
        action: common.black,
        action100: grey[50],
        disabled: grey[600],
        disabled2: grey[700],
        disabled3: grey[500],
        link: blue[400],
        primary: grey[50],
        secondary: grey[300],
        tertiary: grey[500]
    }
};
var dark = {
    background: {
        accent: blue[500],
        action: blue[50],
        actionBold: blue[500],
        level0: common.white,
        level100: grey[50],
        level200: grey[100],
        level300: grey[200],
        level50: grey[25],
        modalOverlay: transparency.gray.dark[400],
        tooltip: grey[700],
        state: {
            disabled1: grey[200],
            disabled2: grey[300],
            disabled3: grey[400],
            disabledTransparent: transparency.gray.dark[100],
            hover0: transparency.gray.dark[50],
            hover100: grey[200],
            hover200: grey[300],
            hover300: grey[400],
            hover50: grey[100],
            selected1: blue[50],
            selected2: blue[100],
            selected3: blue[200],
            selectedAlt: transparency.gray.dark[100],
            selectedTransparent: transparency.blue.light[200],
            selectedTransparent2: transparency.blue.light[300]
        }
    },
    border: {
        action100: blue[300],
        action200: blue[400],
        actionBold: blue[500],
        active: grey[500],
        default: grey[300],
        focus: blue[400],
        heaviest: grey[950],
        heavy: grey[400],
        soft: grey[200]
    },
    icon: {
        action: blue[500],
        action200: blue[300],
        actionAlt: blue[500],
        active: grey[900],
        default: grey[500],
        disabled: grey[400],
        disabled2: grey[500],
        heaviest: common.black,
        heavy: grey[700],
        soft: grey[400]
    },
    text: {
        accent: blue[500],
        action: common.white,
        action100: blue[700],
        disabled: grey[500],
        disabled2: grey[600],
        disabled3: grey[700],
        link: blue[600],
        primary: grey[900],
        secondary: grey[700],
        tertiary: grey[600]
    }
};
export var inverseBackground = function(mode) {
    return isDarkMode(mode) ? dark.background : light.background;
};
export var inverseBorder = function(mode) {
    return isDarkMode(mode) ? dark.border : light.border;
};
export var inverseIcon = function(mode) {
    return isDarkMode(mode) ? dark.icon : light.icon;
};
export var inverseText = function(mode) {
    return isDarkMode(mode) ? dark.text : light.text;
};

//# sourceMappingURL=inverse.js.map